import React, { useState, useEffect, useRef } from 'react';
import { FaRobot, FaPaperPlane, FaUser } from 'react-icons/fa';
import './Chatbot.css';
import axios from 'axios';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content: "Hi! I'm Juan, your assistant for SJDEFI. Ask me about tuition, programs, administrators, or anything else!",
      suggestions: [
        "Who is the president of SJDEFI?",
        "What programs does SJDEFI offer?",
        "How do I enroll online?"
      ],
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const typeMessage = async (content, suggestions = []) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 500)); // Typing delay
    
    const botMessage = { 
      role: 'bot', 
      content, 
      suggestions,
      timestamp: new Date()
    };
    setMessages((prev) => [...prev, botMessage]);
    setIsTyping(false);
  };

  const handleSendMessage = async (e, presetMessage = null) => {
    e.preventDefault();
    const messageToSend = presetMessage || input;
    if (!messageToSend.trim()) return;

    const userMessage = { 
      role: 'user', 
      content: messageToSend,
      timestamp: new Date()
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/chat`, {
        message: messageToSend,
      });
      const botReply = response.data.reply;
      
      // Robust suggestion parsing
      const replyLines = botReply.split('\n').map(line => line.trim()).filter(line => line);
      const suggestionStart = replyLines.findIndex(line => line.startsWith('Suggested questions:'));
      let content = botReply;
      let suggestions = [];
      
      if (suggestionStart !== -1) {
        content = replyLines.slice(0, suggestionStart).join('\n').trim();
        suggestions = replyLines.slice(suggestionStart + 1)
          .filter(line => line.startsWith('- ') && line.replace('- ', '').trim())
          .map(line => line.replace('- ', '').trim())
          .slice(0, 3); // Limit to 3 suggestions
      }
      
      setIsLoading(false);
      await typeMessage(content, suggestions);
    } catch (error) {
      setIsLoading(false);
      await typeMessage("Sorry, I'm having trouble connecting right now. Please try again or contact registrarsoffice@sjdefi.edu.ph!", []);
    }
  };

  const quickQuestions = [
    { label: 'FAQs', query: 'What are the frequently asked questions about SJDEFI?', icon: '❓' },
    { label: 'Tuition', query: 'What are the tuition fees for senior high school at SJDEFI?', icon: '💰' },
    { label: 'School Hours', query: 'What are the office hours of SJDEFI?', icon: '⏰' },
    { label: 'Programs', query: 'What programs does SJDEFI offer?', icon: '📚' },
    { label: 'Enrollment', query: 'How do I enroll at SJDEFI?', icon: '📝' },
    { label: 'Administrators', query: 'Who are the top-level administrators at SJDEFI?', icon: '👥' }
  ];

  const renderMessageContent = (content) => {
    const sections = content.split('\n\n');
    return sections.map((section, index) => {
      if (section.includes('- ')) {
        const lines = section.split('\n');
        const title = lines[0];
        const items = lines.slice(1).filter(line => line.startsWith('- '));
        
        return (
          <div key={index} className="message-section">
            {title && !title.startsWith('- ') && <p className="section-title">{title}</p>}
            <ul className="message-list">
              {items.map((item, i) => (
                <li key={i}>{item.replace('- ', '')}</li>
              ))}
            </ul>
          </div>
        );
      }
      return (
        <div key={index} className="message-section">
          <p>{section}</p>
        </div>
      );
    });
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="chatbot-container">
      <div className={`chatbot-toggle ${isOpen ? 'active' : ''}`} onClick={toggleChatbot}>
        <FaRobot className="toggle-icon" />
        <div className="pulse-ring"></div>
      </div>
      
      <div className={`chatbot-window ${isOpen ? 'open' : 'closed'}`}>
        <div className="chatbot-header">
          <div className="header-info">
            <div className="bot-avatar">
              <FaRobot />
            </div>
            <div className="bot-details">
              <h3>Juan</h3>
              <span className="bot-status">
                <div className="status-dot"></div>
                Online
              </span>
            </div>
          </div>
          <button className="chatbot-close" onClick={toggleChatbot}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <div className="chatbot-messages">
          {messages.map((msg, index) => (
            <div key={index} className={`message-wrapper ${msg.role}`}>
              <div className="message-avatar">
                {msg.role === 'bot' ? <FaRobot /> : <FaUser />}
              </div>
              <div className="message-bubble">
                <div className="message-content">
                  {renderMessageContent(msg.content)}
                </div>
                <div className="message-time">
                  {formatTime(msg.timestamp)}
                </div>
                {msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="suggestions-container">
                    <span className="suggestions-label">Quick replies:</span>
                    <div className="suggestions-grid">
                      {msg.suggestions.map((suggestion, i) => (
                        <button
                          key={i}
                          className="suggestion-chip"
                          onClick={(e) => handleSendMessage(e, suggestion)}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {(isLoading || isTyping) && (
            <div className="message-wrapper bot">
              <div className="message-avatar">
                <FaRobot />
              </div>
              <div className="message-bubble loading">
                <div className="typing-indicator">
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="typing-text">Juan is typing...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="quick-actions">
          <div className="quick-actions-label">Quick questions:</div>
          <div className="quick-buttons-grid">
            {quickQuestions.map((q, index) => (
              <button
                key={index}
                className="quick-action-btn"
                onClick={(e) => handleSendMessage(e, q.query)}
                disabled={isLoading}
              >
                <span className="quick-btn-icon">{q.icon}</span>
                <span className="quick-btn-text">{q.label}</span>
              </button>
            ))}
          </div>
        </div>

        <form className="chat-input-container" onSubmit={handleSendMessage}>
          <div className="input-wrapper">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading}
              className="chat-input"
            />
            <button 
              type="submit" 
              disabled={isLoading || !input.trim()}
              className="send-button"
            >
              <FaPaperPlane />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Chatbot;